import { useCallback, useEffect, useRef, useState } from 'react';

// One clock for every homepage replay panel. `t` is seconds into a loop of
// `length` seconds, ticking only while `active` (the section is on screen)
// and at a coarse step (~12 fps) so React work stays small. `seek(s)` jumps
// the loop — the step cards scrub it on hover/focus.
//
// Frozen (= final frame) whenever motion is not wanted or not seen:
// prefers-reduced-motion, a crawler/prerender (navigator.webdriver), or
// `enabled: false`. The final frame is the finished run, so the prerendered
// HTML and the no-motion page show the result, never frame 0.
export function replayFrozen() {
  if (typeof window === 'undefined') return true;
  if (window.navigator?.webdriver) return true;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
  // phones get the finished run, same as the three.js frames (staticOnly)
  if (window.matchMedia && window.matchMedia('(max-width: 640px)').matches) return true;
  return false;
}

export default function useReplayClock({ length = 30, active = true, enabled = true, hold = 0, step = 1 / 12 } = {}) {
  const frozen = !enabled || replayFrozen();
  const [t, setT] = useState(() => (frozen ? length : 0));
  const startRef = useRef(null); // performance.now() the current lap started
  const lastRef = useRef(-1);

  useEffect(() => {
    if (frozen || !active) return undefined;
    let raf = 0;
    const loop = (now) => {
      if (startRef.current == null) startRef.current = now;
      const total = length + hold;
      let s = ((now - startRef.current) / 1000) % total;
      if (s > length) s = length; // hold on the final frame before the loop restarts
      const q = Math.floor(s / step) * step;
      if (q !== lastRef.current) { lastRef.current = q; setT(q); }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
  }, [frozen, active, length, hold, step]);

  const seek = useCallback((s) => {
    if (frozen) return;
    const now = performance.now();
    startRef.current = now - Math.max(0, Math.min(length, s)) * 1000;
  }, [frozen, length]);

  return { t, seek, frozen, length };
}
